import { Injectable, signal } from '@angular/core'
import { Router } from '@angular/router'
import { environment } from 'src/environments/environment'
import { ToastService } from './toast.service'
import {
  ToastMessage,
  ToastType,
} from '../_components/toast/toast.component'

export type NetworkDiagnosticsState =
  | 'idle'
  | 'checking'
  | 'offline'
  | 'blocked'

interface FailureInfo {
  method: string
  path: string
  userInitiated: boolean
}

interface DismissInfo {
  until: number
  state: 'offline' | 'blocked'
}

const DISMISS_STORAGE_KEY = 'networkBlockDismissed'
const DISMISS_DURATION_MS = 5 * 60 * 1000
const CHECK_TIMEOUT_MS = 5000
const SUPPRESSED_TOAST_INTERVAL_MS = 30 * 1000

export type RetryFeedback = 'still-blocked' | 'still-offline'

@Injectable({
  providedIn: 'root',
})
export class NetworkDiagnosticsService {
  state = signal<NetworkDiagnosticsState>('idle')
  modalVisible = signal(false)
  displayState = signal<'offline' | 'blocked'>('blocked')
  isRetrying = signal(false)
  retryFeedback = signal<RetryFeedback | null>(null)
  lastCheckedAt = signal<Date | null>(null)

  private checking = false
  private queuedFailure: FailureInfo | null = null
  private lastFailure: FailureInfo | null = null
  private portalCheckResult = ''
  private apiProbeResult = ''
  private lastSuppressedToastAt = 0

  constructor(
    private router: Router,
    private toastService: ToastService
  ) {
    window.addEventListener('online', () => this.handleBrowserOnline())
    window.addEventListener('offline', () => this.handleBrowserOffline())
  }

  report(method: string, path: string, userInitiated = false): void {
    const failure: FailureInfo = {
      method,
      path: sanitizePath(path),
      userInitiated,
    }
    this.lastFailure = failure

    if (this.checking) {
      this.queuedFailure = failure
      return
    }

    void this.runDiagnostics(failure)
  }

  dismiss(): void {
    const current = this.displayState()
    this.storeDismiss(current)
    this.hideModal()
  }

  async retry(): Promise<void> {
    if (this.isRetrying()) {
      return
    }

    const failure = this.lastFailure
    if (!failure) {
      return
    }

    this.isRetrying.set(true)
    this.retryFeedback.set(null)
    this.state.set('checking')

    try {
      const result = await this.runChecks(failure)
      this.lastCheckedAt.set(new Date())

      if (result === 'ok') {
        this.hideModal()
        this.clearDismiss()
        this.retryFeedback.set(null)
        this.toastService.triggerToast(
          ToastType.Success,
          ToastMessage.Connection_Restored
        )
        return
      }

      if (this.shouldShowModal(result)) {
        this.showModal(result)
        this.retryFeedback.set(
          result === 'blocked' ? 'still-blocked' : 'still-offline'
        )
        return
      }

      this.hideModal()
    } finally {
      this.isRetrying.set(false)
    }
  }

  notifyApiSuccess(): void {
    if (this.modalVisible()) {
      this.hideModal()
      this.clearDismiss()
    }
  }

  getApiHost(): string {
    try {
      return new URL(environment.apiUrl).host
    } catch {
      return environment.apiUrl
    }
  }

  getDetailsText(): string {
    const failure = this.lastFailure
    const portalResult =
      this.portalCheckResult ||
      (this.state() === 'offline' ? 'unreachable' : 'not checked')
    const apiResult =
      this.apiProbeResult ||
      (this.state() === 'blocked' ? 'failed' : 'not checked')

    return [
      'Speedbox backoffice – network check',
      `Time:       ${new Date().toISOString()}`,
      `API host:   ${this.getApiHost()}`,
      `Failed:     ${failure ? `${failure.method} ${failure.path} (status 0)` : 'unknown'}`,
      `Online:     ${navigator.onLine}`,
      `Portal:     ${portalResult}`,
      `API probe:  ${apiResult}`,
      `Page:       ${this.router.url}`,
      `Browser:    ${navigator.userAgent}`,
    ].join('\n')
  }

  private async runDiagnostics(failure: FailureInfo): Promise<void> {
    this.checking = true
    this.state.set('checking')

    try {
      const result = await this.runChecks(failure)

      if (result === 'ok') {
        this.hideModal()
        return
      }

      if (this.shouldShowModal(result)) {
        this.showModal(result)
      } else {
        this.hideModal()
        this.notifyModalSuppressed(result, failure.userInitiated)
      }
    } finally {
      this.checking = false

      if (this.queuedFailure) {
        this.queuedFailure = null
      }
    }
  }

  private async runChecks(
    failure: FailureInfo
  ): Promise<'ok' | 'offline' | 'blocked'> {
    this.lastFailure = failure
    this.portalCheckResult = ''
    this.apiProbeResult = ''

    if (!navigator.onLine) {
      return 'offline'
    }

    try {
      await fetchWithTimeout(
        `/favicon.ico?probe=${Date.now()}`,
        { cache: 'no-store' },
        CHECK_TIMEOUT_MS
      )
      this.portalCheckResult = 'reachable'
    } catch {
      this.portalCheckResult = 'unreachable'
      return 'offline'
    }

    try {
      await fetchWithTimeout(
        `${environment.apiUrl}/`,
        { mode: 'no-cors', cache: 'no-store' },
        CHECK_TIMEOUT_MS
      )
      this.apiProbeResult = 'ok'
      return 'ok'
    } catch (error) {
      this.apiProbeResult = `failed (${getErrorName(error)})`
      return 'blocked'
    }
  }

  private shouldShowModal(state: 'offline' | 'blocked'): boolean {
    const stored = this.getActiveDismiss()
    if (!stored) {
      return true
    }

    return stored.state !== state
  }

  private notifyModalSuppressed(
    state: 'offline' | 'blocked',
    userInitiated: boolean
  ): void {
    const stored = this.getActiveDismiss()
    if (!stored || stored.state !== state) {
      return
    }

    const now = Date.now()
    if (
      !userInitiated &&
      now - this.lastSuppressedToastAt < SUPPRESSED_TOAST_INTERVAL_MS
    ) {
      return
    }

    if (!userInitiated) {
      this.lastSuppressedToastAt = now
    }
    this.toastService.triggerToast(
      ToastType.Warning,
      ToastMessage.Network_Check_Snoozed_Until,
      null,
      5000,
      { time: formatDismissUntil(stored.until) }
    )
  }

  private getActiveDismiss(): DismissInfo | null {
    const stored = this.getDismissInfo()
    if (!stored || Date.now() >= stored.until) {
      return null
    }

    return stored
  }

  private storeDismiss(state: 'offline' | 'blocked'): void {
    const dismissInfo: DismissInfo = {
      until: Date.now() + DISMISS_DURATION_MS,
      state,
    }
    sessionStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(dismissInfo))
  }

  private getDismissInfo(): DismissInfo | null {
    const raw = sessionStorage.getItem(DISMISS_STORAGE_KEY)
    if (!raw) {
      return null
    }

    try {
      return JSON.parse(raw) as DismissInfo
    } catch {
      return null
    }
  }

  private clearDismiss(): void {
    sessionStorage.removeItem(DISMISS_STORAGE_KEY)
  }

  private handleBrowserOnline(): void {
    if (this.state() === 'offline') {
      void this.retry()
    }
  }

  private handleBrowserOffline(): void {
    if (this.modalVisible() && this.displayState() === 'blocked') {
      this.showModal('offline')
    }
  }

  private showModal(state: 'offline' | 'blocked'): void {
    this.displayState.set(state)
    this.state.set(state)
    this.modalVisible.set(true)
    this.retryFeedback.set(null)
  }

  private hideModal(): void {
    this.modalVisible.set(false)
    this.state.set('idle')
    this.isRetrying.set(false)
    this.retryFeedback.set(null)
  }
}

function sanitizePath(path: string): string {
  const withoutQuery = path.split('?')[0]
  return withoutQuery.replace(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
    '{id}'
  )
}

async function fetchWithTimeout(
  url: string,
  options: globalThis.RequestInit,
  timeoutMs: number
): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs)

  try {
    return await fetch(url, { ...options, signal: controller.signal })
  } finally {
    window.clearTimeout(timeoutId)
  }
}

function getErrorName(error: unknown): string {
  if (error instanceof Error) {
    return error.name
  }
  return 'Error'
}

function formatDismissUntil(until: number): string {
  return new Date(until).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })
}
